import { CSSObject } from "styled-components";
import { useTheme } from "../../theme/ThemeProvider";

const useGridCardStyles = () => {
    const theme = useTheme();
    const styles: {
        container: CSSObject;
        item: CSSObject;
        header: CSSObject;
        title: CSSObject;
        subtitle: CSSObject;
        badge: CSSObject;
        body: CSSObject;
        footer: CSSObject;
        emptyState: CSSObject;
        pagination: CSSObject;
        paginationControls: CSSObject;
        paginationInfo: CSSObject;
        pageSizeSelector: CSSObject;
        pageSizeSelect: CSSObject;
        paginationButton: CSSObject;
        activePage: CSSObject;
        disabledButton: CSSObject;
    } = {
        container: {
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: theme.spacePx.sp04,
            padding: `${theme.spacePx.sp01} 0`,
            fontFamily: theme.font.family,
        },
        item: {
            background: theme.palette.background.main,
            borderRadius: "16px",
            padding: theme.spacePx.sp04,
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.04)",
            border: `1px solid ${theme.palette.background.light}`,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            position: "relative",
            overflow: "hidden",
        },
        header: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: theme.spacePx.sp04,
        },
        title: {
            fontSize: theme.font.sizes.lg,
            fontWeight: theme.font.weights.bold,
            color: theme.palette.text.dark,
            marginBottom: "4px",
            letterSpacing: "-0.02em",
        },
        subtitle: {
            fontSize: "0.9rem",
            color: theme.palette.text.main,
            fontWeight: theme.font.weights.semiBold,
        },
        badge: {
            padding: "6px 12px",
            borderRadius: "8px",
            fontSize: theme.font.sizes.xs,
            fontWeight: theme.font.weights.bold,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
        },
        body: {
            fontSize: theme.font.sizes.md,
            color: theme.palette.text.dark,
            lineHeight: 1.6,
            flex: 1,
            marginBottom: theme.spacePx.sp03,
            fontFamily: theme.font.family,
        },
        footer: {
            paddingTop: theme.spacePx.sp02,
            borderTop: `1px solid ${theme.palette.background.light}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "auto",
        },
        emptyState: {
            textAlign: "center",
            padding: "60px 20px",
            background: theme.palette.background.main,
            color: theme.palette.grey,
            fontSize: theme.font.sizes.md,
            gridColumn: "1 / -1",
        },
        pagination: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: theme.spacePx.sp04,
            marginBottom: theme.spacePx.sp01,
            gridColumn: "1 / -1",
        },
        paginationControls: {
            display: "flex",
            alignItems: "stretch",
            borderRadius: theme.spacePx.sp01,
            border: `1px solid ${theme.palette.divider.light}`,
            overflow: "hidden",
        },
        paginationInfo: {
            fontSize: theme.font.sizes.md,
            color: theme.palette.black,
            fontWeight: theme.font.weights.regular,
        },
        pageSizeSelector: {
            display: "flex",
            alignItems: "center",
            gap: theme.spacePx.sp02,
            fontSize: theme.font.sizes.md,
            color: theme.palette.text.main,
            fontWeight: theme.font.weights.regular,
        },
        pageSizeSelect: {
            width: "auto",
            display: "inline-block",
            marginRight: "0.5em",
            color: theme.palette.text.main,
            height: "36px",
            border: `1px solid ${theme.palette.divider.light}`,
            borderRadius: "4px",
            padding: "0 8px",
            backgroundColor: theme.palette.background.main,
            fontSize: theme.font.sizes.md,
            cursor: "pointer",
            outline: "none",
            fontFamily: theme.font.family,
        },
        paginationButton: {
            position: "relative",
            backgroundColor: theme.palette.background.main,
            border: "none",
            borderRight: `1px solid ${theme.palette.divider.light}`,
            padding: "6px 12px",
            borderRadius: "0",
            color: theme.palette.black,
            fontWeight: theme.font.weights.regular,
            fontSize: theme.font.sizes.md,
            cursor: "pointer",
            lineHeight: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "36px",
            textDecoration: "none",
            fontFamily: theme.font.family,
            "&:last-child": {
                borderRight: "none",
            },
            "&:hover:not(:disabled)": {
                backgroundColor: theme.palette.background.light,
                color: theme.palette.black,
                zIndex: 2,
            },
        },
        activePage: {
            backgroundColor: theme.palette.black,
            color: theme.palette.white,
            zIndex: 3,
        },
        disabledButton: {
            opacity: 0.65,
            cursor: "not-allowed",
            pointerEvents: "none",
            backgroundColor: theme.palette.disabled.light,
        },
    };

    return styles;
};

export default useGridCardStyles;